import React, { useContext, useState } from 'react';

import { useTranslation } from 'react-i18next';
import gql from 'graphql-tag';

import { AppContext } from 'lane-shared/contexts';
import { getClient } from 'lane-shared/apollo';
import { ChannelMerchantFragment } from 'lane-shared/graphql/fragments';
import { pause } from 'async-timing';

import ControlMenu from 'components/general/ControlMenu';
import ErrorMessage from 'components/general/ErrorMessage';
import Loading from 'components/general/Loading';
import Panel from 'components/layout/Panel';
import { H4, M } from 'components/typography';

import stripeConnectButtonImage from 'static/img/stripe-connect-button.png';

import styles from './StripeConnectSetup.scss';

const createMerchantAccountMutation = gql`
  ${ChannelMerchantFragment}

  mutation createMerchantAccountM($merchantAccount: ChannelMerchantInput!) {
    addChannelMerchantAccount(merchantAccount: $merchantAccount) {
      ...ChannelMerchantFragment
    }
  }
`;

export default function StripeConnectSetup({ channel }: any) {
  const { t } = useTranslation();
  const { whitelabel } = useContext(AppContext);
  const whiteLabelName = whitelabel?.name;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function createMerchantAccount() {
    if (loading) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await pause(1000);

      const { data } = await getClient().mutate({
        mutation: createMerchantAccountMutation,
        variables: {
          merchantAccount: {
            channel: {
              _id: channel._id,
            },
            enabled: true,
          },
        },
      });

      window.location = data.addChannelMerchantAccount.onboardingLink.url;
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.StripeConnectSetup} data-is-loading={loading}>
      <ErrorMessage error={error} />
      <Panel className={styles.message}>
        <H4 mb={2}>
          {t('web.admin.channel.merchantAccount.stripeConnect.title')}
        </H4>
        <M mb={4} variant="secondary">
          {t(
            'web.admin.channel.merchantAccount.stripeConnect.description',
            { whiteLabelName }
          )}
        </M>
        <ControlMenu>
          <Loading className={styles.loading} />
          <img
            className={styles.stripeButton}
            src={stripeConnectButtonImage}
            alt={t(
              'web.admin.channel.merchantAccount.stripeConnect.imageAlt',
              { whiteLabelName }
            )}
            role="button"
            tabIndex={0}
            onClick={createMerchantAccount}
          />
        </ControlMenu>
      </Panel>
    </div>
  );
}
